import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Code2, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { UserMenu } from './UserMenu';

export const Header = ({ onUploadClick }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem('user');
    setIsAuthenticated(!!user);
  }, []);

  // Re-check authentication when component mounts or localStorage changes
  useEffect(() => {
    const checkAuth = () => {
      const user = localStorage.getItem('user');
      setIsAuthenticated(!!user);
    };
    
    // Check immediately
    checkAuth();

    // Set up interval to check periodically
    const interval = setInterval(checkAuth, 500);

    window.addEventListener('storage', checkAuth);
    window.addEventListener('focus', checkAuth);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('focus', checkAuth);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <Code2 className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">APIfromSQL</h1>
              <p className="text-xs text-muted-foreground">Intelligence-Powered Code Generator</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Features
            </a>
            <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              How It Works
            </a>
            <a href="#benefits" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Benefits
            </a>
            <a href="#video" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Tutorial
            </a>
          </nav>

          {/* Auth Buttons / User Menu */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Button 
                  onClick={onUploadClick}
                  className="bg-primary hover:bg-primary-dark text-primary-foreground font-medium px-6 shadow-md hover:shadow-lg transition-all"
                >
                  Upload SQL
                </Button>
                <UserMenu />
              </>
            ) : (
              <>
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="font-medium"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/signup')}
                  className="bg-primary hover:bg-primary-dark text-primary-foreground font-medium px-6 shadow-md hover:shadow-lg transition-all"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <div className="flex flex-col gap-6 mt-8">
                <a href="#features" className="text-base font-medium text-foreground hover:text-primary transition-colors">
                  Features
                </a>
                <a href="#how-it-works" className="text-base font-medium text-foreground hover:text-primary transition-colors">
                  How It Works
                </a>
                <a href="#benefits" className="text-base font-medium text-foreground hover:text-primary transition-colors">
                  Benefits
                </a>
                <a href="#video" className="text-base font-medium text-foreground hover:text-primary transition-colors">
                  Tutorial
                </a>
                <div className="border-t border-border pt-4">
                  {isAuthenticated ? (
                    <>
                      <Button onClick={onUploadClick} className="bg-primary text-primary-foreground w-full mb-3">
                        Upload SQL
                      </Button>
                      <Button variant="outline" onClick={() => {
                        localStorage.removeItem('user');
                        navigate('/login');
                      }} className="w-full">
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => navigate('/login')} variant="outline" className="w-full mb-3">
                        Sign In
                      </Button>
                      <Button onClick={() => navigate('/signup')} className="bg-primary text-primary-foreground w-full">
                        Get Started
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
